<script setup>
import ChatTicketAction from '@frontJs/components/actions/projectManagement/tasks/ChatTicketAction.vue';
import SaveTicketAction from '@frontJs/components/actions/projectManagement/tasks/SaveTicketAction.vue';
import ToggleTicketDoneAtAction from '@frontJs/components/actions/projectManagement/tasks/ToggleTicketDoneAtAction.vue';
import ToggleTicketValidatedAtAction from '@frontJs/components/actions/projectManagement/tasks/ToggleTicketValidatedAtAction.vue';
import ChatCircleTextIcon from '@iconify-vue/ph/chat-circle-text';
import CheckIcon from '@iconify-vue/ph/check';
import CurrencyEurIcon from '@iconify-vue/ph/currency-eur-fill';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import XIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    tenant,
    all = false,
    toBeProcessed,
    inProgress,
    toBeApproved,
    pendingCustomerResponse,
    completed,
    callback,
} = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
    toBeProcessed: {
        type: Boolean,
        required: false,
    },
    inProgress: {
        type: Boolean,
        required: false,
    },
    toBeApproved: {
        type: Boolean,
        required: false,
    },
    pendingCustomerResponse: {
        type: Boolean,
        required: false,
    },
    completed: {
        type: Boolean,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'fullName',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.fullName,
                    length: 180,
                    minWidth: 280,
                    maxWidth: 560,
                    ...(props.row.original.isBillable
                        ? {
                              tooltipContent: 'Facturable',
                              tooltipIcon: CurrencyEurIcon,
                              tooltipColor: 'yellow',
                              tooltipPlacement: 'top',
                          }
                        : {}),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.tenantUser
                        ? 'Créée par le client'
                        : 'Créée par Apikom',
                    type: props.row.original.isCustomerTask
                        ? 'primary'
                        : 'secondary',
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'users',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Responsable(s)',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.users
                        .map((user) => user.fullName)
                        .join(', '),
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'createdBy',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Créé par',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenantUser
                        ? props.row.original.tenantUser.fullName
                        : props.row.original.centralUser
                          ? props.row.original.centralUser.fullName
                          : '',
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'linkedTenantUsers',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Assigné à',
                }),
            cell: (props) =>
                props.row.original.linkedTenantUsers
                    ? h(TextCell, {
                          text: props.row.original.linkedTenantUsers
                              .map(
                                  (linkedTenantUser) =>
                                      linkedTenantUser.fullName,
                              )
                              .join(', '),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'priority',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Priorité',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.priority.label,
                    type:
                        props.row.original.priority.value === 'low'
                            ? 'success'
                            : props.row.original.priority.value === 'medium'
                              ? 'warning'
                              : 'danger',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'createdAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Créé le',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.createdAt,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'doneAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date<br/>validation client',
                }),
            cell: (props) =>
                props.row.original.doneAt
                    ? h(TextCell, {
                          text: props.row.original.doneAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'validatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date<br/>validation Apikom',
                }),
            cell: (props) =>
                props.row.original.validatedAt
                    ? h(TextCell, {
                          text: props.row.original.validatedAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastUpdatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière MAJ le',
                }),
            cell: (props) =>
                props.row.original.lastUpdatedAt
                    ? h(TextCell, {
                          text: props.row.original.lastUpdatedAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                ChatTicketAction,
                                {
                                    ticket: props.row.original,
                                    callback: () => {
                                        if (callback) {
                                            callback();
                                        }

                                        updateData();
                                    },
                                },
                                {
                                    button: ({ setIsOpen }) =>
                                        h(ActionButton, {
                                            square: true,
                                            size: 'small',
                                            callback: () => setIsOpen(true),
                                            title: 'Chat',
                                            icon: ChatCircleTextIcon,
                                            ping:
                                                props.row.original
                                                    .hasNewCustomerAnswer &&
                                                !completed,
                                        }),
                                },
                            ),
                            h(
                                SaveTicketAction,
                                {
                                    ticket: props.row.original,
                                    callback: () => {
                                        if (callback) {
                                            callback();
                                        }

                                        updateData();
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            square: true,
                                            size: 'small',
                                            title: 'Modifier',
                                            icon: PencilSimpleIcon,
                                            type: 'primary',
                                        }),
                                },
                            ),
                            ...(toBeProcessed ||
                            inProgress ||
                            pendingCustomerResponse ||
                            completed
                                ? [
                                      h(
                                          ToggleTicketDoneAtAction,
                                          {
                                              ticket: props.row.original,
                                              callback: () => {
                                                  if (callback) {
                                                      callback();
                                                  }

                                                  updateData();
                                              },
                                          },
                                          {
                                              button: ({ action, loading }) =>
                                                  h(ActionButton, {
                                                      size: 'small',
                                                      square: true,
                                                      title:
                                                          props.row.original
                                                              .doneAt === null
                                                              ? 'Terminer le ticket'
                                                              : 'Rouvrir le ticket',
                                                      type: 'secondary',
                                                      callback: () => action(),
                                                      icon:
                                                          props.row.original
                                                              .doneAt === null
                                                              ? CheckIcon
                                                              : XIcon,
                                                      loading,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                            ...(toBeApproved
                                ? [
                                      h(
                                          ToggleTicketValidatedAtAction,
                                          {
                                              ticket: props.row.original,
                                              callback: () => {
                                                  if (callback) {
                                                      callback();
                                                  }

                                                  updateData();
                                              },
                                          },
                                          {
                                              button: ({ action, loading }) =>
                                                  h(ActionButton, {
                                                      size: 'small',
                                                      square: true,
                                                      title:
                                                          props.row.original
                                                              .validatedAt ===
                                                          null
                                                              ? 'Valider le ticket'
                                                              : 'Invalider le ticket',
                                                      type: 'secondary',
                                                      callback: () => action(),
                                                      icon:
                                                          props.row.original
                                                              .validatedAt ===
                                                          null
                                                              ? CheckIcon
                                                              : XIcon,
                                                      loading,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.task.postTickets', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    data: {
        tenantId: tenant ? tenant.id : null,
        toBeProcessed,
        inProgress,
        toBeApproved,
        pendingCustomerResponse,
        completed,
    },
    columnVisibility: {
        doneAt: completed || toBeApproved,
        validatedAt: completed,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.task.ticket', {
                        tenantId: tenant ? tenant.id : null,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
